import React, { useEffect, useState } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { ExpandMoreOutlined } from "@mui/icons-material";
import ObjectCard from "./ObjectCard";
import { tokens } from "../theme/theme";

/**
 * The card view of the listings table.
 *
 * Paged on the client in blocks of PAGE, and that is not cosmetic: every card
 * costs one S3 LIST to find its cover photo, so rendering all 100 rows the
 * table holds would fire 100 requests the moment the view is opened. Showing
 * 24 and letting the realtor ask for more keeps that proportional to what they
 * actually look at.
 *
 * "Show more" walks the already-loaded items first; only when those run out
 * does it ask the panel for the next page from DynamoDB (`loadNext`), which is
 * the same button the table's pagination triggers — and only while the panel
 * says there is one (`hasMore`). A filtered search returns everything that
 * matches in one go, so offering to load more there fetched unrelated rows.
 */

const PAGE = 24;

export default function ObjectCardGrid({
  items,
  s3Client,
  actions,
  onOpen,
  loadNext,
  hasMore,
}) {
  const [shown, setShown] = useState(PAGE);

  // A new search resets the list; showing page 4 of the previous results would
  // look like the search had returned nothing.
  useEffect(
    function () {
      setShown(PAGE);
    },
    [items.length === 0]
  );

  const visible = items.slice(0, shown);
  const hasMoreLoaded = shown < items.length;
  const canLoadMore = !!loadNext && !!hasMore;

  const showMore = function () {
    if (hasMoreLoaded) {
      setShown(shown + PAGE);
    } else if (canLoadMore) {
      // Out of loaded rows: pull the next page from the database, then reveal it.
      loadNext();
      setShown(shown + PAGE);
    }
  };

  return (
    // px matches the tables' `mx: { xs: 1, md: 2 }`, so switching views does
    // not shift the left edge of the page.
    <Container maxWidth={false} sx={{ px: { xs: 1, md: 2 }, pb: 4 }}>
      <Box
        sx={{
          display: "grid",
          // minmax(0, 1fr) so a long street name cannot widen its column.
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
            xl: "repeat(5, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {visible.map(function (item) {
          return (
            <ObjectCard
              key={item.id}
              item={item}
              s3Client={s3Client}
              actions={actions}
              onOpen={onOpen}
            />
          );
        })}
      </Box>

      <Stack alignItems="center" spacing={1} sx={{ pt: 3 }}>
        <Typography variant="caption" sx={{ color: tokens.ink400 }}>
          Показано {visible.length} з {items.length}
        </Typography>
        {(hasMoreLoaded || canLoadMore) && (
          <Button
            variant="outlined"
            startIcon={<ExpandMoreOutlined />}
            onClick={showMore}
          >
            {/* Two different things wear one button in the table's pagination,
                and the difference matters here: revealing rows already in hand
                is instant, fetching the next DynamoDB page is a round trip. */}
            {hasMoreLoaded ? "Показати ще" : "Завантажити ще з бази"}
          </Button>
        )}
      </Stack>
    </Container>
  );
}
